import React, { useState, useRef, useEffect, useMemo, useContext } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  IconButton,
  Paper,
  Dialog,
  AppBar,
  Toolbar,
  Slide,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import {
  getQueryParam,
  anyWordStartsWith,
  equalsIgnoreCase,
  getCustomerName,
  formatPhoneNumber,
  capitalize,
  getStoreFilterStreets,
  orderMatchesStoreFilter,
} from "../../common/util";
import { useLocation } from "react-router-dom";
import bkstApi from "../../api";
import { UserContext } from "../../context/UserContext";
import { useNavigate } from "react-router";
import SearchIcon from "@mui/icons-material/Search";
import BDatePicker from "../../common/input/BDatePicker";
import LinearProgress from "@mui/material/LinearProgress";
import ItemFilter from "../../planner/component/ItemFilter";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { useReactToPrint } from "react-to-print";
import { bakeStreetLogo } from "../../common/constants";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function canOpenFullOrder(role) {
  return role !== "kitchen" && role !== "clerk";
}

export default function ProductionPlan(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [fromDt, setFromDt] = useState("");
  const [toDt, setToDt] = useState("");

  const [orders, setOrders] = useState("");
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState("");
  const [itemFilter, setItemFilter] = useState({});
  const [storeFilter, setStoreFilter] = useState({});
  const [filtered, setFiltered] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const storeLocations = user?.config?.storeLocation || [];
  const storeStreets = getStoreFilterStreets(user);
  const showStoreFilter = storeStreets.length > 1;

  const printRef = useRef();
  const summaryPrintRef = useRef();
  const dataGridApiRef = useRef(null);

  const printSummary = useReactToPrint({
    content: () => summaryPrintRef.current,
    documentTitle: `Production_Plan_${fromDt}_${toDt}`,
  });

  // Check params to identify search criteria
  useEffect(() => {
    let [qFrom, qTo] = [
      getQueryParam(location.search, "from"),
      getQueryParam(location.search, "to"),
    ];
    if (!qFrom || !qTo) {
      // reset dates
      qFrom = moment().format("YYYY-MM-DD");
      qTo = moment().add(7, "days").format("YYYY-MM-DD");
    }
    search(qFrom, qTo);
  }, [location.search]);

  useEffect(() => {
    if (toDt < fromDt) {
      setToDt("");
    } else if (fromDt && toDt && moment(toDt).diff(moment(fromDt), "days") > 31) {
      setToDt("");
    }
  }, [fromDt, toDt]);

  const runDateSearch = (e) => {
    e.preventDefault();
    navigate(`/planner/production?from=${fromDt}&to=${toDt}`);
  };

  const search = (fromDate, toDate) => {
    setLoading(true);
    bkstApi.get(`/my/planner/production?fromDt=${fromDate}&toDt=${toDate}`).then((res) => {
      setOrders(res.data.orders);

      setFromDt(fromDate);
      setToDt(toDate);

      setLoading(false);
    });
  };

  const exportToCSV = () => {
    if (dataGridApiRef.current) {
      const categoryName = selectedCategory?.title?.replace(/\s+/g, "_") || "";
      dataGridApiRef.current.exportDataAsCsv({
        fileName: `Production_Plan_${fromDt}_${toDt}_${categoryName}`,
        allColumns: true,
      });
    }
  };

  const visibleOrders = useMemo(() => {
    const list = orders || [];
    return list.filter((o) =>
      orderMatchesStoreFilter(o, storeFilter?.store?.value, storeLocations)
    );
  }, [orders, storeFilter, storeLocations]);

  // Transform orders data into DataGrid rows
  const rows = useMemo(() => {
    if (!visibleOrders || visibleOrders.length === 0) return [];

    const flatItems = [];
    let rowIndex = 0;

    visibleOrders.forEach((order) => {
      if (order.items && order.items.length > 0) {
        const customerName = getCustomerName(order.customer) || "";
        // Store raw phone number to prevent backtick in CSV export
        const customerPhone = order.customer?.phone || "";

        order.items.forEach((item) => {
          let notes = item.variant?.notes?.name;
          if (!notes && item.description) {
            notes = item.description.replace(/<[^>]*>?/g, "");
          }

          flatItems.push({
            id: `${order.id}-${item.title}-${rowIndex++}`,
            orderId: order.id,
            quantity: item.quantity,
            itemName: item.title,
            type: item.productId ? "standard" : item.total ? "custom" : "standard",
            productId: item.productId || "",
            total: item.total || item.price,
            variant: item.variant || {},
            customerName: customerName,
            customerPhone: customerPhone,
            itemNotes: notes,
          });
        });
      }
    });

    return flatItems;
  }, [visibleOrders]);

  // Create summary rows grouped by productId (or title if no productId)
  const summaryRows = useMemo(() => {
    // Use filtered data if available, otherwise use all rows
    const dataSource = filtered && filtered.length > 0 ? filtered : rows;

    if (!dataSource || dataSource.length === 0) return [];

    const groupedData = {};

    dataSource.forEach((row) => {
      // Use productId as the grouping key, or title if no productId
      const groupKey = row.productId || row.itemName;

      if (!groupedData[groupKey]) {
        groupedData[groupKey] = {
          id: groupKey,
          groupKey: groupKey,
          productId: row.productId || "",
          itemName: row.itemName,
          title:
            row.type === "standard"
              ? capitalize(row.itemName)
              : `Custom ${capitalize(row.itemName)}`,
          count: 0,
        };
      }

      groupedData[groupKey].count += Number(row.quantity) || 0;
    });

    return Object.values(groupedData);
  }, [rows, filtered]);

  // Detail rows filtered by selected category
  const detailRows = useMemo(() => {
    if (!selectedCategory) return [];

    // Filter rows to show only items matching the selected category
    const categoryRows = rows.filter((row) => {
      const rowGroupKey = row.productId || row.itemName;
      return rowGroupKey === selectedCategory.groupKey;
    });

    // Apply additional variant filters if any
    if (Object.keys(itemFilter).length > 0) {
      return categoryRows.filter((row) => {
        for (const [key, filterValue] of Object.entries(itemFilter)) {
          if (filterValue?.value) {
            // Handle variant filters only (item filter is removed)
            const variantValue = row.variant?.[key];
            let valueToMatch = "";

            if (typeof variantValue === "object" && variantValue?.name) {
              valueToMatch = String(variantValue.name);
            } else if (variantValue !== null && variantValue !== undefined) {
              valueToMatch = String(variantValue);
            }

            if (!equalsIgnoreCase(valueToMatch, filterValue.value)) {
              return false;
            }
          }
        }
        return true;
      });
    }

    return categoryRows;
  }, [rows, selectedCategory, itemFilter]);

  // Summary DataGrid columns
  const summaryColumns = [
    {
      field: "count",
      headerName: "Quantity",
      width: 75,
      type: "number",
    },
    {
      field: "title",
      headerName: "Item Name",
      flex: 2,
      minWidth: 200,
    },
  ];

  // Get variant keys that have data in detail rows (selected category)
  const detailVariantKeys = useMemo(() => {
    if (!detailRows || detailRows.length === 0) return [];

    const keys = new Set();
    detailRows.forEach((row) => {
      if (row.variant) {
        Object.keys(row.variant).forEach((key) => {
          // Only include keys that have actual data
          const value = row.variant[key];
          if (value !== null && value !== undefined && value !== "" && key !== "notes") {
            keys.add(key);
          }
        });
      }
    });

    return Array.from(keys);
  }, [detailRows]);

  // Total quantity for detail grid header (stable number dep avoids any loop with detailColumns)
  const detailTotal = useMemo(
    () => detailRows.reduce((sum, row) => sum + +row.quantity, 0),
    [detailRows]
  );

  // Create columns for detail DataGrid (derived from detailRows)
  const detailColumns = useMemo(() => {
    // Hidden columns for export only
    const customerNameColumn = {
      field: "customerName",
      headerName: "Customer Name",
      hide: true,
    };

    const customerPhoneColumn = {
      field: "customerPhone",
      headerName: "Customer Phone",
      hide: true,
      valueFormatter: (params) => {
        // Prefix with space to prevent Excel from treating phone starting with + as formula
        // This prevents the leading quote/apostrophe in CSV export
        const phone = params.value || "";
        return ` ${formatPhoneNumber(phone)}`;
      },
    };

    const itemNotesColumn = {
      field: "itemNotes",
      headerName: "Notes",
      hide: true,
    };

    const itemNameColumn = {
      field: "itemName",
      headerName: "Item Name",
      flex: 1,
      minWidth: 200,
    };

    // Add dynamic columns for each variant key from detail rows
    const variantColumns = detailVariantKeys.map((key) => ({
      field: `variant_${key}`,
      headerName: key
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => {
        const variant = params.row.variant || {};
        const variantValue = variant[key];

        if (!variantValue) return "";

        // Return the name if it's an object, otherwise return the value directly
        if (typeof variantValue === "object") {
          return variantValue.name || "";
        }

        return variantValue;
      },
    }));

    const quantityHeader = detailTotal > 0 ? `Quantity (${detailTotal})` : "Quantity";
    const quantityColumn = {
      field: "quantity",
      headerName: quantityHeader,
      width: 135,
      type: "number",
    };

    const orderLinkColumn = {
      field: "orderId",
      headerName: "",
      width: 50,
      sortable: false,
      disableExport: true,
      renderCell: (params) => (
        <IconButton component="a" href={`/order/${params.row.orderId}`} target="_blank">
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      ),
    };

    return [
      quantityColumn,
      itemNameColumn,
      ...variantColumns,
      ...(canOpenFullOrder(user?.role) ? [orderLinkColumn] : []),
      itemNotesColumn,
      customerNameColumn,
      customerPhoneColumn,
    ];
  }, [detailVariantKeys, detailTotal, user?.role]);

  // Apply filters to rows
  useEffect(() => {
    let filteredRows = rows;

    // Apply text query filter for item name
    if (query) {
      filteredRows = filteredRows.filter((row) => anyWordStartsWith(row.itemName, query));
    }

    // Apply item filters for all variant properties
    if (Object.keys(itemFilter).length > 0) {
      filteredRows = filteredRows.filter((row) => {
        for (const [key, filterValue] of Object.entries(itemFilter)) {
          if (filterValue?.value) {
            // Handle itemName filter separately
            if (key === "item") {
              if (!equalsIgnoreCase(row.itemName, filterValue.value)) {
                return false;
              }
            } else {
              // Handle variant filters
              const variantValue = row.variant?.[key];
              let valueToMatch = "";

              if (typeof variantValue === "object" && variantValue?.name) {
                valueToMatch = String(variantValue.name);
              } else if (variantValue !== null && variantValue !== undefined) {
                valueToMatch = String(variantValue);
              }

              if (!equalsIgnoreCase(valueToMatch, filterValue.value)) {
                return false;
              }
            }
          }
        }
        return true;
      });
    }

    setFiltered(filteredRows);
  }, [rows, query, itemFilter]);

  // Get filter options for variant properties (excluding notes and item) from detail data
  const filterConfig = useMemo(() => {
    // Only show filters when a category is selected
    if (!selectedCategory) return [];

    const filterOptionsMap = {};

    // Use detailRows for filter options (items in selected category)
    const dataSource = detailRows.length > 0 ? detailRows : [];

    // Priority filter order (excluding itemName since selection is via summary table)
    const priorityOrder = ["size", "flavor", "filling", "icing"];

    // Collect all unique values for each variant property
    dataSource.forEach((row) => {
      if (row.variant) {
        Object.entries(row.variant).forEach(([key, value]) => {
          // Skip "notes" and "inscription" from filter options
          if (key === "notes" || key === "inscription") {
            return;
          }

          if (!filterOptionsMap[key]) {
            filterOptionsMap[key] = new Set();
          }

          // Extract the name if it's an object, otherwise use the value directly
          let filterValue = "";
          if (typeof value === "object" && value?.name) {
            filterValue = value.name;
          } else if (value !== null && value !== undefined) {
            filterValue = value;
          }

          // Convert to string and add to set
          if (filterValue) {
            filterOptionsMap[key].add(String(filterValue));
          }
        });
      }
    });

    // Convert to array format and sort
    const allFilters = Object.entries(filterOptionsMap)
      .map(([key, valueSet]) => ({
        name: key,
        options: Array.from(valueSet),
        key: key,
      }))
      .filter((config) => config.options.length > 0);

    // Separate priority and other filters
    const priorityFilters = allFilters.filter((f) => priorityOrder.includes(f.key));
    const otherFilters = allFilters.filter((f) => !priorityOrder.includes(f.key));

    // Sort priority filters by the defined order
    priorityFilters.sort((a, b) => {
      const indexA = priorityOrder.indexOf(a.key);
      const indexB = priorityOrder.indexOf(b.key);
      return indexA - indexB;
    });

    // Sort other filters by number of options
    otherFilters.sort((a, b) => b.options.length - a.options.length);

    // Combine: priority first, then sorted by options
    return [...priorityFilters, ...otherFilters];
  }, [selectedCategory, detailRows]);

  if (loading) {
    return <LinearProgress />;
  } else {
    return (
      <Container maxWidth="md">
        <Box ref={printRef}>
          <Box my={4}>
            <Typography variant="h1" gutterBottom>
              Production Plan
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              A high-level summary of items (with total quantity) scheduled for fulfillment within
              the selected date range. Click on any item to see an itemized view with variant
              details & filter options.
            </Typography>
            <Box my={2}>
              <form onSubmit={runDateSearch}>
                <Box py={2} align="center" sx={{ display: "flex", gap: "10px" }}>
                  <BDatePicker
                    value={fromDt}
                    variant="filled"
                    label="From Date"
                    onChange={(d) => setFromDt(d)}
                    required
                    autoOk
                    hideIcon={true}
                    minDate={moment().subtract(1, "year").toDate()}
                  />
                  <BDatePicker
                    value={toDt}
                    variant="filled"
                    label="To Date"
                    onChange={(d) => setToDt(d)}
                    required
                    autoOk
                    hideIcon={true}
                    minDate={fromDt ? moment(fromDt).toDate() : undefined}
                    maxDate={fromDt ? moment(fromDt).add(31, "days").toDate() : undefined}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{ height: "56px" }}
                  >
                    <SearchIcon />
                  </Button>
                </Box>
              </form>
            </Box>
            {showStoreFilter && orders && orders.length > 0 && (
              <Box my={2}>
                <ItemFilter
                  config={[{ name: "store", options: storeStreets }]}
                  data={orders}
                  onChange={(o) => setStoreFilter({ ...storeFilter, ...o })}
                />
              </Box>
            )}
          </Box>

          {rows.length > 0 && (
            <>
              {/* Summary DataGrid */}
              <Box my={2}>
                <Box
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    Summary
                  </Typography>
                  <IconButton onClick={printSummary} title="Print Summary">
                    <PrintOutlinedIcon />
                  </IconButton>
                </Box>
                <Box sx={{ height: "auto", width: "100%" }}>
                  <DataGrid
                    rows={summaryRows}
                    columns={summaryColumns}
                    autoHeight
                    disableRowSelectionOnClick
                    disableColumnMenu
                    initialState={{
                      sorting: {
                        sortModel: [{ field: "title", sort: "asc" }],
                      },
                    }}
                    onRowClick={(params) => {
                      setSelectedCategory(params.row);
                      setItemFilter({});
                    }}
                    sx={{
                      "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid #ECECEC",
                        paddingY: "10px",
                      },
                      "& .MuiDataGrid-row": {
                        cursor: "pointer",
                      },
                      "& .MuiDataGrid-row:nth-of-type(odd)": {
                        backgroundColor: "#FAFAFA",
                      },
                      "& .MuiDataGrid-row:nth-of-type(even)": {
                        backgroundColor: "#FFF",
                      },
                      "& .MuiDataGrid-cell:focus": {
                        outline: "none",
                      },
                      "& .MuiDataGrid-cell:focus-within": {
                        outline: "none",
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Hidden printable summary table */}
              <Box sx={{ display: "none" }}>
                <Box ref={summaryPrintRef} sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h5" gutterBottom>
                        Production Plan Summary
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {moment(fromDt).format("MMM D, YYYY")} -{" "}
                        {moment(toDt).format("MMM D, YYYY")}
                      </Typography>
                    </Box>
                    <img alt="logo" src={bakeStreetLogo} style={{ maxHeight: "24pt" }} />
                  </Box>
                  <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "16px" }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid #333" }}>
                        <th style={{ textAlign: "left", padding: "8px", width: "80px" }}>
                          <Typography variant="overline">Quantity</Typography>
                        </th>
                        <th style={{ textAlign: "left", padding: "8px" }}>
                          <Typography variant="overline">Item Name</Typography>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...summaryRows]
                        .sort((a, b) => a.title.localeCompare(b.title))
                        .map((row) => (
                          <tr key={row.id} style={{ borderBottom: "1px solid #ddd" }}>
                            <td style={{ padding: "8px" }}>{row.count}</td>
                            <td style={{ padding: "8px" }}>{row.title}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Details Dialog - Fullscreen */}
              <Dialog
                fullScreen
                open={Boolean(selectedCategory)}
                onClose={() => {
                  setSelectedCategory(null);
                  setItemFilter({});
                }}
                TransitionComponent={Transition}
              >
                <AppBar sx={{ position: "relative" }}>
                  <Toolbar>
                    <IconButton
                      edge="start"
                      color="inherit"
                      onClick={() => {
                        setSelectedCategory(null);
                        setItemFilter({});
                      }}
                      aria-label="back"
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                      {selectedCategory?.title} ({detailTotal}) | {moment(fromDt).format("MMM D")} -{" "}
                      {moment(toDt).format("MMM D")}
                    </Typography>
                    <IconButton color="inherit" onClick={exportToCSV} title="Export to CSV">
                      <FileDownloadOutlinedIcon />
                    </IconButton>
                    <IconButton
                      color="inherit"
                      onClick={() => {
                        setSelectedCategory(null);
                        setItemFilter({});
                      }}
                      aria-label="close"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Toolbar>
                </AppBar>
                <Box sx={{ p: 2 }}>
                  <Paper elevation={0}>
                    <Box py={0.75}>
                      <ItemFilter
                        config={filterConfig}
                        onChange={(filterUpdate) => {
                          setItemFilter({ ...itemFilter, ...filterUpdate });
                        }}
                      />
                    </Box>
                  </Paper>
                  <Box sx={{ height: "auto", width: "100%" }}>
                    <DataGrid
                      apiRef={dataGridApiRef}
                      rows={detailRows}
                      columns={detailColumns}
                      autoHeight
                      getRowHeight={() => "auto"}
                      disableRowSelectionOnClick
                      disableColumnMenu
                      initialState={{
                        sorting: {
                          sortModel: [{ field: "itemName", sort: "asc" }],
                        },
                        columns: {
                          columnVisibilityModel: {
                            customerName: false,
                            customerPhone: false,
                            itemNotes: false,
                          },
                        },
                      }}
                      sx={{
                        "& .MuiDataGrid-cell": {
                          borderBottom: "1px solid #ECECEC",
                          paddingY: "10px",
                        },
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: "#ECECEC",
                        },
                        "& .MuiDataGrid-row:nth-of-type(odd)": {
                          backgroundColor: "#FAFAFA",
                        },
                        "& .MuiDataGrid-row:nth-of-type(even)": {
                          backgroundColor: "#FFF",
                        },
                        "& .MuiDataGrid-cell:focus": {
                          outline: "none",
                        },
                        "& .MuiDataGrid-cell:focus-within": {
                          outline: "none",
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Dialog>
            </>
          )}
        </Box>
      </Container>
    );
  }
}
